/**
 * KCIS™ Pro — Pathway override editor (Sprint 6B4).
 *
 * The engine's `pathway_assigned` is shown read-only. The practitioner
 * may submit an adjacent override that is stored in
 * `nutrient_pathway_overrides` — engine output is NEVER mutated.
 * Reason is recommended; backend writes an immutable audit entry either way.
 */
import { useEffect, useState } from "react";
import { Loader2, Route, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";
import { Button } from "@/components/ui/button";

const PATHWAY_OPTIONS = [
  { value: "clinic", label: "Clinic" },
  { value: "coaching", label: "Coaching" },
  { value: "university", label: "University" },
  { value: "comfort_care", label: "Comfort care" },
  { value: "hybrid", label: "Hybrid" },
  { value: "integrated", label: "Integrated" },
  { value: "kcis", label: "KCIS clinical" },
];

export default function OverrideEditor({
  assessmentId,
  enginePathway,
  currentOverride,
  canOverride,
  onChanged,
}) {
  const [selected, setSelected] = useState(currentOverride?.overridden_pathway || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelected(currentOverride?.overridden_pathway || "");
  }, [currentOverride?.overridden_pathway]);

  const submit = async () => {
    if (!selected) {
      toast.error("Pick a pathway to override to.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch(`/nutrient/${assessmentId}/override/pathway`, {
        new_pathway: selected,
        reason: reason.trim() || null,
      });
      if (r?.data?.ok) {
        toast.success("Pathway override saved");
        setReason("");
        onChanged?.();
      } else {
        toast.error(r?.data?.error || "Could not save override");
      }
    } catch (err) {
      if (err?.response?.status === 503) {
        toast.message("Pathway override is temporarily disabled.");
      } else if (err?.response?.status === 403) {
        toast.error("You do not have permission to override pathway.");
      } else {
        toast.error(formatApiError(err, "Could not save override"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="practitioner-override-editor"
      className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4"
    >
      <div className="flex items-center gap-2">
        <Route className="w-5 h-5 text-purple-900" />
        <div>
          <div className="font-medium text-slate-900">Pathway override</div>
          <div className="text-xs text-slate-500">
            Engine assignment is preserved · override is stored side-by-side
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="kcis-overline text-slate-500">Engine pathway</div>
          <div
            data-testid="override-engine-pathway"
            className="mt-1 font-medium text-slate-900"
          >
            {enginePathway || "—"}
          </div>
        </div>
        <div className="rounded-xl border border-purple-200 bg-purple-50/40 p-3">
          <div className="kcis-overline text-purple-900">Current override</div>
          <div
            data-testid="override-current-pathway"
            className="mt-1 font-medium text-slate-900"
          >
            {currentOverride?.overridden_pathway || "None"}
          </div>
          {currentOverride?.overridden_by ? (
            <div className="text-xs text-slate-600 mt-1">
              by {currentOverride.overridden_by}
              {currentOverride.overridden_at
                ? ` · ${new Date(currentOverride.overridden_at).toLocaleString()}`
                : ""}
            </div>
          ) : null}
        </div>
      </div>

      {canOverride ? (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {PATHWAY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                data-testid={`override-pathway-option-${opt.value}`}
                onClick={() => setSelected(opt.value)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  selected === opt.value
                    ? "bg-purple-900 text-white border-purple-900"
                    : "bg-white text-slate-700 border-slate-200 hover:border-purple-400"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <textarea
            data-testid="override-pathway-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Clinical reasoning (optional but recommended — logged immutably)"
            className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:border-purple-400 focus:outline-none min-h-[72px]"
          />
          <div className="flex justify-end">
            <Button
              data-testid="override-pathway-submit"
              onClick={submit}
              disabled={saving || !selected}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Save override
            </Button>
          </div>
        </div>
      ) : (
        <div
          data-testid="override-pathway-readonly"
          className="text-xs text-slate-500 italic"
        >
          Read-only · your role cannot override pathway.
        </div>
      )}
    </div>
  );
}
